import { useCallback, useEffect, useSyncExternalStore } from 'react';

export type ThemePreference = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

const KEY = 'gilt.theme';
const listeners = new Set<() => void>();

function read(): ThemePreference {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'dark' || v === 'light' || v === 'system') return v;
  } catch {
    /* storage unavailable (private mode) */
  }
  return 'dark';
}

const media = () => (typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: light)') : null);

export function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') return media()?.matches ? 'light' : 'dark';
  return pref;
}

/** Apply the stored theme to <html> — call once before React renders to avoid a flash. */
export function applyTheme(pref: ThemePreference = read()) {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(pref);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.style.colorScheme = resolved;
}

export function setThemePreference(pref: ThemePreference) {
  try {
    localStorage.setItem(KEY, pref);
  } catch {
    /* ignore */
  }
  applyTheme(pref);
  listeners.forEach((l) => l());
}

/** Current preference + setter; keeps `system` in sync with the OS setting. */
export function useTheme(): { preference: ThemePreference; resolved: ResolvedTheme; setPreference: (p: ThemePreference) => void } {
  const preference = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    read,
    () => 'dark' as ThemePreference,
  );
  useEffect(() => {
    if (preference !== 'system') return;
    const m = media();
    if (!m) return;
    const onChange = () => {
      applyTheme('system');
      listeners.forEach((l) => l());
    };
    m.addEventListener('change', onChange);
    return () => m.removeEventListener('change', onChange);
  }, [preference]);
  const setPreference = useCallback((p: ThemePreference) => setThemePreference(p), []);
  return { preference, resolved: resolveTheme(preference), setPreference };
}
